import { Reveal } from "@/components/common/Reveal";
import { Eyebrow } from "@/components/common/Section";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { usePrefersReducedMotion } from "@/hooks/useReducedMotion";

/* Promises + their companion screens. Images uploaded as image1..image4.png
 * are portrait phone mockups that ship with a tan/magenta bezel-external
 * background. We hide that in the browser by placing each image inside a
 * phone-shaped mask + scaling the image so only the screen fills the mask. */
const PROMISES = [
  {
    n: "01",
    claim: "Report a repair in seconds.",
    line: "Snap a photo, tap send. Most issues diagnosed from the picture before a truck ever rolls.",
    img: "https://customer-assets-wrfwihn1.emergentagent.net/job_roam-lodge/artifacts/cbcycmqh_image1.png",
    caption: "Maintenance · live status",
  },
  {
    n: "02",
    claim: "A human inside the hour.",
    line: "Every resident message answered within 60 minutes, 8am–8pm, seven days a week.",
    img: "https://customer-assets-wrfwihn1.emergentagent.net/job_roam-lodge/artifacts/v8a5rris_image2.png",
    caption: "Messages · real humans",
  },
  {
    n: "03",
    claim: "Owners hear from us first.",
    line: "A report every Monday at 10am. You'll never type 'any update?' again.",
    img: "https://customer-assets-wrfwihn1.emergentagent.net/job_roam-lodge/artifacts/zjusqa24_image3.png",
    caption: "The Monday Report",
  },
  {
    n: "04",
    claim: "Every invoice, visible.",
    line: "The actual repair invoice, attached to every statement. You see what we see.",
    img: "https://customer-assets-wrfwihn1.emergentagent.net/job_roam-lodge/artifacts/ooszjwtf_image4.png",
    caption: "Owner statement · invoices attached",
  },
];

export const RoamPromise = () => {
  return (
    <section
      className="grain relative"
      style={{ backgroundColor: "#24382E" }}
      data-nav-theme="dark"
      data-testid="home-promise"
    >
      <div className="relative z-10 mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-36">
        <PromiseSlider />
      </div>
    </section>
  );
};

/* ============================================================
 * PromiseSlider — the whole interactive stage.
 *   Left  : 4 tappable rows (01–04), title + sub-line
 *   Right : one persistent phone frame; the SCREEN swaps in/out
 * On mobile: stage first, list after; auto-advance every 4.5s
 * until the user interacts.
 * ============================================================ */
const PromiseSlider = () => {
  const reduced = usePrefersReducedMotion();
  const [active, setActive] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const intentTimerRef = useRef(null);

  // Track viewport bucket (mobile vs desktop) for tilt + auto-advance.
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mql.matches);
    on();
    mql.addEventListener("change", on);
    return () => mql.removeEventListener("change", on);
  }, []);

  // Preload every screen so swaps are instant.
  useEffect(() => {
    PROMISES.forEach((p) => {
      const im = new Image();
      im.src = p.img;
    });
  }, []);

  // Mobile auto-advance until first interaction.
  useEffect(() => {
    if (!isMobile || hasInteracted || reduced) return;
    const id = setInterval(() => {
      setActive((a) => (a + 1) % PROMISES.length);
    }, 4500);
    return () => clearInterval(id);
  }, [isMobile, hasInteracted, reduced]);

  // 60ms hover-intent delay — prevents flicker on cursor sweep.
  const scheduleActive = (i) => {
    clearTimeout(intentTimerRef.current);
    intentTimerRef.current = setTimeout(() => setActive(i), 60);
  };
  const cancelSchedule = () => clearTimeout(intentTimerRef.current);
  const commitActive = (i) => {
    clearTimeout(intentTimerRef.current);
    setActive(i);
    setHasInteracted(true);
  };

  return (
    <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-10">
      {/* ---- SECTION HEADER (always on top) ---- */}
      <div className="order-1 md:col-span-7 md:order-1">
        <Reveal>
          <Eyebrow color="#AEC4C7">The ROAM promise</Eyebrow>
        </Reveal>
        <Reveal delay={0.05}>
          <h2
            className="font-display mt-7 max-w-[14ch]"
            style={{
              fontSize: "clamp(36px,5vw,72px)",
              color: "#F8F5EF",
              lineHeight: 1.02,
            }}
          >
            Managed like we{" "}
            <span className="italic" style={{ color: "#E3B268" }}>
              live there
            </span>{" "}
            too.
          </h2>
        </Reveal>
      </div>

      {/* ---- STAGE — sits below the header on mobile, right column
              on desktop spanning both rows ---- */}
      <div className="order-2 md:col-span-5 md:col-start-8 md:row-span-2 md:row-start-1 md:self-center">
        <PhoneStage active={active} reduced={reduced} isMobile={isMobile} />
      </div>

      {/* ---- LIST OF PROMISES (below stage on mobile, below header on desktop) ---- */}
      <div className="order-3 md:col-span-7 md:col-start-1 md:row-start-2">
        <ul className="mt-0 md:mt-6" role="tablist" aria-label="ROAM promise features">
          {PROMISES.map((p, i) => (
            <PromiseRow
              key={p.n}
              index={i}
              promise={p}
              active={active === i}
              onHoverIntent={() => scheduleActive(i)}
              onLeave={cancelSchedule}
              onFocus={() => setActive(i)}
              onClick={() => commitActive(i)}
            />
          ))}
        </ul>
      </div>
    </div>
  );
};

/* ============================================================
 * PromiseRow — a single accessible row in the left list.
 * Active states:
 *   • number: larch (#E3B268), scale 1.06, gold-drop-shadow glow
 *   • title:  opacity 1
 *   • rule:   2px larch line draws in under the row (0.35s)
 * Inactive:
 *   • number: soft gold #C89A4E, no scale
 *   • title:  opacity 0.65
 * ============================================================ */
const PromiseRow = ({
  index,
  promise,
  active,
  onHoverIntent,
  onLeave,
  onFocus,
  onClick,
}) => {
  return (
    <li>
      <Reveal delay={index * 0.09}>
        <button
          type="button"
          role="tab"
          aria-selected={active}
          aria-controls="promise-stage"
          onMouseEnter={onHoverIntent}
          onMouseLeave={onLeave}
          onFocus={onFocus}
          onBlur={onLeave}
          onClick={onClick}
          data-testid={`promise-row-${promise.n}`}
          className="relative block w-full cursor-pointer border-t py-8 text-left focus:outline-none"
          style={{ borderColor: "rgba(200,154,78,0.28)" }}
        >
          <div className="grid grid-cols-12 items-baseline gap-4 md:gap-6">
            {/* number */}
            <motion.span
              className="font-display col-span-2"
              animate={{
                color: active ? "#E3B268" : "#C89A4E",
                scale: active ? 1.06 : 1,
                filter: active
                  ? "drop-shadow(0 6px 18px rgba(227,178,104,0.55))"
                  : "drop-shadow(0 0 0 rgba(227,178,104,0))",
              }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              style={{
                fontSize: "clamp(40px,6vw,84px)",
                lineHeight: 0.8,
                transformOrigin: "left center",
                display: "inline-block",
              }}
            >
              {promise.n}
            </motion.span>
            {/* copy */}
            <div className="col-span-10">
              <motion.h3
                className="font-display"
                animate={{ opacity: active ? 1 : 0.65 }}
                transition={{ duration: 0.3 }}
                style={{
                  fontSize: "clamp(24px,3vw,38px)",
                  color: "#F8F5EF",
                  lineHeight: 1.08,
                }}
              >
                {promise.claim}
              </motion.h3>
              <motion.p
                className="body-measure mt-2 text-[16px]"
                animate={{ opacity: active ? 0.85 : 0.5 }}
                transition={{ duration: 0.3 }}
                style={{ color: "rgba(237,233,224,1)" }}
              >
                {promise.line}
              </motion.p>
            </div>
          </div>
          {/* active rule — draws in under the row */}
          <motion.span
            aria-hidden
            className="absolute -bottom-[1px] left-0 h-[2px]"
            initial={false}
            animate={{ width: active ? "100%" : "0%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            style={{ backgroundColor: "#E3B268" }}
          />
        </button>
      </Reveal>
    </li>
  );
};

/* ============================================================
 * PhoneStage — the persistent phone frame + swapping screen.
 * The bezel, glow, and floor shadow NEVER move; only the image
 * inside the mask swaps via AnimatePresence(mode="popLayout").
 * Ambient (desktop only, non-reduced):
 *   • levitate 8px vertically, 6s ease-in-out infinite
 *   • tilt toward cursor ±3° (rotateX / rotateY, spring)
 * ============================================================ */
const PhoneStage = ({ active, reduced, isMobile }) => {
  const current = PROMISES[active];

  // Cursor-tilt springs — inactive on mobile / reduced-motion.
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [3, -3]), {
    stiffness: 100,
    damping: 14,
  });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-3, 3]), {
    stiffness: 100,
    damping: 14,
  });

  const onMouseMove = (e) => {
    if (isMobile || reduced) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onMouseLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div
      className="relative mx-auto"
      style={{
        maxWidth: 320,
        perspective: 1200,
      }}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      data-testid="promise-stage"
      id="promise-stage"
    >
      {/* larch glow behind the device */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 -z-0 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: 400,
          height: 400,
          background:
            "radial-gradient(circle, rgba(200,154,78,0.28) 0%, rgba(200,154,78,0.08) 45%, transparent 75%)",
          filter: "blur(30px)",
          opacity: 0.6,
        }}
      />

      {/* Levitation wrapper */}
      <motion.div
        className="relative"
        animate={
          reduced || isMobile
            ? {}
            : { y: [-4, 4, -4] }
        }
        transition={
          reduced || isMobile
            ? {}
            : { duration: 6, repeat: Infinity, ease: "easeInOut" }
        }
      >
        {/* Tilt wrapper */}
        <motion.div
          style={{
            transformStyle: "preserve-3d",
            rotateX: reduced || isMobile ? 0 : rx,
            rotateY: reduced || isMobile ? 0 : ry,
          }}
        >
          {/* PHONE — bezel + inner mask */}
          <div
            className="relative mx-auto"
            style={{
              width: "100%",
              aspectRatio: "9 / 19.5",
              padding: 10, // bezel thickness
              borderRadius: 44,
              backgroundColor: "#14231B",
              boxShadow:
                "0 40px 60px -25px rgba(0,0,0,0.75), 0 12px 24px -12px rgba(0,0,0,0.45)",
            }}
            data-testid="phone-frame"
          >
            {/* Inner mask — the SCREEN. Rounded 34px sits nicely inside a 44px bezel with 10px pad. */}
            <div
              className="relative h-full w-full overflow-hidden"
              style={{
                borderRadius: 34,
                boxShadow:
                  "inset 0 0 0 1px rgba(0,0,0,0.15), inset 0 6px 18px rgba(0,0,0,0.18)",
                backgroundColor: "#F4EDE0",
              }}
            >
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.div
                  key={active}
                  className="absolute inset-0"
                  style={{
                    /* backgroundSize just over 100% zooms into the centre
                     * of the source image, cropping out the tan/magenta
                     * backdrop while keeping the whole app screen
                     * (status bar → footer buttons) visible. The phone
                     * chassis corners tuck neatly behind our rounded
                     * mask so the app appears to sit inside our own
                     * bezel. All four PNGs share the same phone
                     * placement so one value works everywhere. */
                    backgroundImage: `url("${current.img}")`,
                    backgroundSize: "128%",
                    backgroundPosition: "center 48%",
                    backgroundRepeat: "no-repeat",
                  }}
                  role="img"
                  aria-label={current.caption}
                  data-testid={`phone-screen-${current.n}`}
                  initial={
                    reduced
                      ? { opacity: 0 }
                      : {
                          opacity: 0,
                          y: 24,
                          scale: 0.98,
                          filter: "blur(4px)",
                        }
                  }
                  animate={
                    reduced
                      ? {
                          opacity: 1,
                          transition: { duration: 0.2 },
                        }
                      : {
                          opacity: 1,
                          y: 0,
                          scale: 1,
                          filter: "blur(0px)",
                          transition: {
                            duration: 0.4,
                            ease: [0.22, 1, 0.36, 1],
                          },
                        }
                  }
                  exit={
                    reduced
                      ? { opacity: 0, transition: { duration: 0.2 } }
                      : {
                          opacity: 0,
                          y: -24,
                          filter: "blur(4px)",
                          transition: { duration: 0.28 },
                        }
                  }
                />
              </AnimatePresence>
            </div>
          </div>
        </motion.div>

        {/* elliptical floor shadow */}
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 -translate-x-1/2"
          style={{
            bottom: -24,
            width: "80%",
            height: 28,
            background:
              "radial-gradient(ellipse at center, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 55%, transparent 80%)",
            filter: "blur(8px)",
          }}
        />
      </motion.div>

      {/* CAPTION + PROGRESS DOTS */}
      <div className="relative mt-10 flex flex-col items-center gap-4">
        <div className="relative h-4">
          <AnimatePresence mode="wait" initial={false}>
            <motion.p
              key={current.n}
              initial={reduced ? { opacity: 0 } : { opacity: 0, y: 6 }}
              animate={
                reduced
                  ? { opacity: 1, transition: { duration: 0.2 } }
                  : { opacity: 1, y: 0, transition: { duration: 0.3 } }
              }
              exit={
                reduced
                  ? { opacity: 0, transition: { duration: 0.2 } }
                  : { opacity: 0, y: -6, transition: { duration: 0.2 } }
              }
              className="label-caps text-[10px]"
              style={{
                color: "rgba(174,196,199,0.85)",
                letterSpacing: "0.24em",
              }}
              data-testid="promise-caption"
            >
              {current.caption}
            </motion.p>
          </AnimatePresence>
        </div>

        <div
          className="flex items-center gap-2"
          role="tablist"
          data-testid="promise-progress"
        >
          {PROMISES.map((p, i) => (
            <span
              key={p.n}
              aria-current={i === active}
              className="block h-1.5 rounded-full transition-all duration-300"
              style={{
                width: i === active ? 20 : 6,
                backgroundColor:
                  i === active ? "#E3B268" : "rgba(237,233,224,0.32)",
              }}
              data-testid={`promise-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default RoamPromise;
